import React, { useState, useEffect } from 'react';
import { Calendar, Users, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const heroImages = [
  "https://images.unsplash.com/photo-1455587734955-081b22074882?auto=format&fit=crop&w=1280&q=60", // Hotel Building Night
  "/ChatGPT Image Apr 25, 2026, 05_06_39 PM.png", // User image 1
  "/39df97eb-f90d-4052-917c-3555ac56a8e8.png", // User image 2
  "https://images.unsplash.com/photo-1542314831-c6a4d14b8fcb?auto=format&fit=crop&w=1280&q=60", // Luxury Lobby/Bar
  "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=1280&q=60"  // Clean Suite 2
];

const Hero = () => {
  const [currentBg, setCurrentBg] = useState(0);

  // Preload images to prevent loading delays
  useEffect(() => {
    heroImages.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  useEffect(() => {
    const duration = currentBg === 0 ? 6000 : 4000; // 6s for first, 4s for others
    const timeout = setTimeout(() => {
      setCurrentBg((prev) => (prev + 1) % heroImages.length);
    }, duration);
    return () => clearTimeout(timeout);
  }, [currentBg]);

  return (
    <section id="home" className="relative h-screen flex flex-col items-center justify-center overflow-hidden bg-black">
      
      {/* Dynamic Background Carousel with Fast Fade-through-Black */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentBg}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="absolute inset-0 z-0"
        >
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url("${heroImages[currentBg]}")` }}
          />
        </motion.div>
      </AnimatePresence>

      {/* Subtle top/bottom gradient for text legibility and "Shade" removal */}
      <div className="absolute inset-0 z-1 bg-gradient-to-b from-black/60 via-transparent to-black/60 pointer-events-none" />

      {/* Main Content */}
      <div className="container-custom relative z-10 w-full flex flex-col items-center justify-center h-full pt-24 pb-12">
        
        {/* Subtle top accent */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="flex items-center gap-4 mb-8"
        >
          <div className="w-12 h-[1px] bg-orange-400"></div>
          <span className="text-orange-300 text-xs tracking-[0.4em] uppercase font-medium">GPHUB Luxury Suites</span>
          <div className="w-12 h-[1px] bg-orange-400"></div>
        </motion.div>
        
        {/* Imposing Typography */}
        <motion.h1 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.7 }}
          className="text-5xl md:text-7xl lg:text-8xl font-serif text-white text-center leading-tight mb-6"
        >
          A New Era of <br className="hidden md:block" />
          <span className="italic text-transparent bg-clip-text bg-gradient-to-r from-orange-200 via-orange-400 to-yellow-500 font-light">Refined Luxury</span>
        </motion.h1>
        
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.9 }}
          className="text-white font-light text-sm md:text-base tracking-widest max-w-2xl text-center mb-16 md:mb-24"
        >
          EXPERIENCE UNPARALLELED HOSPITALITY IN THE HEART OF HYDERABAD
        </motion.p>

        {/* Sleek Pill-Shaped Booking Widget */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.1 }}
          className="w-full max-w-5xl relative group z-20 mt-auto md:mt-0"
        >
          <div className="absolute -inset-2 bg-gradient-to-r from-orange-500/20 via-yellow-500/20 to-orange-500/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-700 opacity-0 group-hover:opacity-100"></div>
          
          <div className="relative bg-black/40 backdrop-blur-2xl p-2 md:p-3 rounded-3xl md:rounded-full border border-white/20 shadow-[0_20px_50px_rgba(0,0,0,0.5)] flex flex-col md:flex-row items-center w-full">
            
            {/* Check In */}
            <div className="flex-1 flex items-center gap-4 px-6 py-4 w-full border-b md:border-b-0 md:border-r border-white/10 group/item cursor-pointer hover:bg-white/10 transition-colors rounded-t-2xl md:rounded-l-full md:rounded-tr-none">
              <Calendar className="text-orange-400 w-5 h-5 group-hover/item:text-orange-300 transition-colors" />
              <div className="flex flex-col items-start w-full">
                <span className="text-[10px] uppercase tracking-[0.2em] text-gray-300 mb-1 font-medium">Check In</span>
                <span className="text-white text-sm font-medium tracking-wide">Select Date</span>
              </div>
            </div>

            {/* Check Out */}
            <div className="flex-1 flex items-center gap-4 px-6 py-4 w-full border-b md:border-b-0 md:border-r border-white/10 group/item cursor-pointer hover:bg-white/10 transition-colors">
              <Calendar className="text-orange-400 w-5 h-5 group-hover/item:text-orange-300 transition-colors" />
              <div className="flex flex-col items-start w-full">
                <span className="text-[10px] uppercase tracking-[0.2em] text-gray-300 mb-1 font-medium">Check Out</span>
                <span className="text-white text-sm font-medium tracking-wide">Select Date</span>
              </div>
            </div>

            {/* Guests */}
            <div className="flex-1 flex items-center gap-4 px-6 py-4 w-full group/item cursor-pointer hover:bg-white/10 transition-colors md:rounded-r-none">
              <Users className="text-orange-400 w-5 h-5 group-hover/item:text-orange-300 transition-colors" />
              <div className="flex flex-col items-start w-full">
                <span className="text-[10px] uppercase tracking-[0.2em] text-gray-300 mb-1 font-medium">Guests</span>
                <select className="bg-transparent border-none outline-none text-white text-sm font-medium tracking-wide cursor-pointer w-full appearance-none">
                  <option className="bg-luxury-black text-white">1 Room, 2 Guests</option>
                  <option className="bg-luxury-black text-white">2 Rooms, 4 Guests</option>
                  <option className="bg-luxury-black text-white">Family Suite</option>
                </select>
              </div>
            </div>

            {/* CTA Button */}
            <button 
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-orange-500 hover:bg-orange-400 text-white text-xs md:text-sm font-bold tracking-[0.2em] uppercase py-5 px-8 flex items-center justify-center gap-2 transition-all duration-300 w-full md:w-auto rounded-b-2xl md:rounded-full shadow-[0_0_20px_rgba(249,115,22,0.3)] hover:shadow-[0_0_30px_rgba(249,115,22,0.5)] shrink-0 group/btn m-1 md:m-0"
            >
              <span className="relative z-10">Check Rates</span>
              <ArrowRight className="w-4 h-4 ml-1 group-hover/btn:translate-x-1 transition-transform" />
            </button>
            
          </div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div 
        animate={{ opacity: [0.3, 1, 0.3] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10 pointer-events-none hidden md:flex"
      >
        <span className="text-[9px] uppercase tracking-[0.3em] text-white/70">Scroll</span>
        <div className="w-[1px] h-12 bg-gradient-to-b from-white to-transparent" />
      </motion.div>

    </section>
  );
};

export default Hero;
